/**
 * RES.RELEASE guarantee (R2) — Compact closed structure.
 *
 * Neutralizes authority duplication (G4) by enforcing that allocated
 * resources are eventually released. Resource leaks are prevented by
 * tracking every allocation-release pair.
 *
 * Agda source: Kernel/RES/RELEASE/Neutralization.agda
 * Realization fidelity: RUNTIME-SUPPLEMENTED (release tracking at runtime)
 * Safety class: Parametric (acquired immunity)
 * Neutralization mode: CONTAINED — bounded by resource budget
 * Tier: 3 (quantitative)
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { Brand, Guarantee } from "../foundation/types.js";
import { RES_RELEASE_META } from "../shared/theorem-meta.js";
export { RES_RELEASE_META };

// ── Structure: Compact closed ──────────────────────────────────

/** A managed resource with guaranteed release via using(). */
export type ManagedResource<R> = Brand<
  { readonly id: string; readonly resource: R; readonly released: boolean },
  "ManagedResource"
>;

/** Allocate a managed resource. Must be used with release() or using(). */
export function managedAllocate<R>(id: string, resource: R): ManagedResource<R> {
  return { id, resource, released: false } as unknown as ManagedResource<R>; // ESCAPE: brand erasure to construct the managed resource; managedAllocate is the only authorized entry point
}

/** Release a managed resource. Double-release throws. */
export function release<R>(managed: ManagedResource<R>): void {
  const m = managed as unknown as { id: string; resource: R; released: boolean }; // ESCAPE: brand erasure to mutate released flag; this is the only place where the flag is set
  if (m.released) {
    throw new Error(`Resource ${m.id} already released — double-release prevented by RES.RELEASE`);
  }
  m.released = true;
}

/** Use a resource with guaranteed release (RAII pattern). */
export function using<R, T>(managed: ManagedResource<R>, fn: (resource: R) => T): T {
  try {
    const m = managed as unknown as { resource: R }; // ESCAPE: brand erasure to read resource for the user fn; using() guarantees release in finally
    return fn(m.resource);
  } finally {
    release(managed);
  }
}

export interface ResReleaseViolation {
  readonly __brand: "ResReleaseViolation";
  readonly kind: "resource-leak";
}

export interface ResReleaseStructure {
  readonly trackedResources: ReadonlySet<string>;
  readonly verifyAllReleased: () => boolean;
}

export function createResReleaseGuarantee(
  structure: ResReleaseStructure,
): Guarantee<ResReleaseStructure, ResReleaseViolation> {
  return {
    structure,
    neutralize: (_violation: ResReleaseViolation): never => {
      throw new Error(
        "RES.RELEASE violation is contained by resource tracking: " + "using() guarantees release",
      );
    },
    meta: RES_RELEASE_META,
  };
}
